import { EntryTake } from '../types';

export interface ExportStatusResponse {
  success: boolean;
  status: 'ready' | 'processing' | 'error';
  downloadUrl?: string;
  exportKey?: string;
  error?: string;
  cached?: boolean;
}

export interface SocialShareLinks {
  canonicalUrl: string;
  whatsapp: string;
  twitter: string;
  facebook: string;
  telegram: string;
  reddit: string;
  linkedin: string;
  canNativeShare: boolean;
  nativeShare: () => Promise<boolean>;
}

// In-flight mutex mapping for export requests to prevent concurrent redundant calls (Rule 1 & 2)
const inFlightExportRequests = new Map<string, Promise<ExportStatusResponse>>();

/**
 * Computes a short deterministic hash based on take IDs, timings, and volumes
 */
export function computeTakesHash(takes: any[]): string {
  if (!takes || takes.length === 0) return 'clean';
  const raw = takes
    .map((t) => `${t.id || t.takeNumber}_${t.lineStart}_${t.lineEnd}_${t.volume || 1}`)
    .join('|');
  let h = 0;
  for (let i = 0; i < raw.length; i++) {
    h = (h << 5) - h + raw.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36);
}

/**
 * Serializes takes for export, converting in-memory blob URLs into Base64 buffers for Modal
 */
export async function prepareTakesForExport(takes: EntryTake[]): Promise<any[]> {
  const active = takes.filter((t) => (t as any).isActive ?? true);
  return Promise.all(
    active.map(async (t) => {
      let audioUrl = t.audioUrl || '';
      let audioBase64: string | undefined = undefined;

      if (audioUrl.startsWith('blob:')) {
        try {
          const resp = await fetch(audioUrl);
          const arrayBuffer = await resp.arrayBuffer();
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          const chunkSize = 8192;
          for (let i = 0; i < bytes.length; i += chunkSize) {
            binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize) as any);
          }
          audioBase64 = btoa(binary);
        } catch (e) {
          console.warn('[exportService] Error reading blob take:', e);
        }
      }

      return {
        id: t.id,
        takeNumber: t.takeNumber,
        characterLabel: t.characterLabel,
        lineStart: Number(t.lineStart ?? 0),
        lineEnd: Number(t.lineEnd ?? 0),
        duration: Number(t.duration ?? 0),
        volume: Number(t.volume ?? 1.0),
        audioUrl: (t as any).remoteAudioUrl || (audioUrl.startsWith('/api/media/') ? audioUrl : ''),
        audioBase64
      };
    })
  );
}

/**
 * Canonical shareable web URL for a SoulSync clip
 */
export function getClipShareUrl(clipId: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://soulsync.app';
  return `${origin}/share/${encodeURIComponent(clipId)}`;
}

/**
 * Triggers video export + concatenation of the 3s SoulSync logo outro.
 * Adheres to Cloudflare Concurrency Mutex and Quota rules.
 */
export async function requestClipExport(
  clipId: string,
  takes: EntryTake[] = [],
  creatorName = 'Creator'
): Promise<ExportStatusResponse> {
  const cleanId = clipId.trim();
  if (!cleanId) {
    return { success: false, status: 'error', error: 'Clip ID is required' };
  }

  // Mutex check: reuse in-flight request if already querying
  if (inFlightExportRequests.has(cleanId)) {
    return inFlightExportRequests.get(cleanId)!;
  }

  const exportPromise = (async (): Promise<ExportStatusResponse> => {
    try {
      const activeTakes = await prepareTakesForExport(takes);
      const takesHash = computeTakesHash(activeTakes);
      const exportKey = `exports/${cleanId}_${takesHash}_export.mp4`;

      const res = await fetch('/api/export/clip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clipId: cleanId,
          creatorName,
          takes: activeTakes,
          exportKey
        })
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        return {
          success: false,
          status: 'error',
          error: `Export request failed (${res.status}): ${errorText || res.statusText}`
        };
      }

      const data = (await res.json()) as ExportStatusResponse;
      return {
        ...data,
        exportKey
      };
    } catch (err: any) {
      console.warn('[exportService] requestClipExport error:', err);
      return {
        success: false,
        status: 'error',
        error: err.message || 'Failed to communicate with export service'
      };
    } finally {
      inFlightExportRequests.delete(cleanId);
    }
  })();

  inFlightExportRequests.set(cleanId, exportPromise);
  return exportPromise;
}

/**
 * Polls export readiness every 3.0 seconds (respecting rule: >= 2.5s heartbeat).
 */
export async function pollExportReadiness(
  clipId: string,
  onStatusUpdate?: (statusText: string) => void,
  maxAttempts = 15,
  exportKey?: string
): Promise<ExportStatusResponse> {
  let attempts = 0;
  const pollIntervalMs = 3000;

  while (attempts < maxAttempts) {
    attempts++;
    onStatusUpdate?.(`Stitching audio stems & SoulSync outro... (${attempts * 3}s)`);

    try {
      let queryUrl = `/api/export/clip?clipId=${encodeURIComponent(clipId)}`;
      if (exportKey) {
        queryUrl += `&exportKey=${encodeURIComponent(exportKey)}`;
      }
      const res = await fetch(queryUrl);
      if (res.ok) {
        const data = (await res.json()) as ExportStatusResponse;
        if (data.status === 'ready' && data.downloadUrl) {
          onStatusUpdate?.('Export ready! Starting download...');
          return data;
        }
        if (data.status === 'error') {
          return data;
        }
      }
    } catch (e) {
      console.warn('[exportService] Poll attempt warning:', e);
    }

    // Wait 3.0s before next poll
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  return {
    success: false,
    status: 'error',
    error: 'Export timed out after 45 seconds. Please try again.'
  };
}

/**
 * Generates ready-to-use share URLs and native share handler for all major platforms.
 */
export function generateSocialShareLinks(params: {
  clipId: string;
  title: string;
  creatorName?: string;
  customMessage?: string;
}): SocialShareLinks {
  const canonicalUrl = getClipShareUrl(params.clipId);
  const author = params.creatorName ? `@${params.creatorName}` : 'SoulSync Creator';
  const custom = params.customMessage?.trim();

  const shareText = custom
    ? `${custom} — Dubbed by ${author} on SoulSync! 🎙️✨`
    : `Check out "${params.title}" dubbed by ${author} on SoulSync! 🎙️✨ Can you voice it?`;

  const encodedUrl = encodeURIComponent(canonicalUrl);
  const encodedText = encodeURIComponent(shareText);
  const encodedCombined = encodeURIComponent(`${shareText} ${canonicalUrl}`);

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const nativeShare = async (): Promise<boolean> => {
    if (!canNativeShare) return false;
    try {
      await navigator.share({
        title: `SoulSync: ${params.title}`,
        text: shareText,
        url: canonicalUrl
      });
      return true;
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.warn('[exportService] nativeShare error:', err);
      }
      return false;
    }
  };

  return {
    canonicalUrl,
    whatsapp: `https://api.whatsapp.com/send?text=${encodedCombined}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}&hashtags=SoulSync,VoiceActing,Duet`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    canNativeShare,
    nativeShare
  };
}
